import React, {useEffect, useRef, useState} from 'react';

import {useCurrentUserPendingTasksQuery} from 'client/SignUp/NewSignUp/signUp.queries.generated';

import ForceOnboarding from './ForceOnboarding';

interface ForceOnboardingGateProps {
  children: React.ReactNode;
}

/**
 * Fetches the current user's pending signup tasks from GraphQL and hands them to
 * ForceOnboarding, which routes the user through the outstanding onboarding steps.
 *
 * Fails open, but never guesses: when the query errors or returns no user, the app
 * (children) renders and an onboarding branch is never chosen from defaulted data.
 * A network-level failure holds the loading state through one retry before failing
 * open, so children mount exactly once either way. Failures are deliberately not
 * reported here; `apollo/errorLink.ts` owns GraphQL error reporting and its policy
 * treats network errors as non-actionable.
 */
export default function ForceOnboardingGate({children}: ForceOnboardingGateProps) {
  const {
    data: pendingData,
    loading,
    error,
    refetch
  } = useCurrentUserPendingTasksQuery({
    fetchPolicy: 'network-only'
  });
  const hasRetriedRef = useRef(false);
  const [retrySettled, setRetrySettled] = useState(false);

  const networkFailed = Boolean(error?.networkError);

  useEffect(() => {
    if (networkFailed && !hasRetriedRef.current) {
      hasRetriedRef.current = true;
      refetch()
        .catch((retryError: unknown) => {
          logger.warn(
            'currentUserPendingTasks retry failed; rendering without the onboarding gate: %o',
            retryError
          );
        })
        .finally(() => setRetrySettled(true));
    }
  }, [networkFailed, refetch]);

  const pendingLoading = loading || (networkFailed && !retrySettled);
  const currentUser = pendingData?.currentUser;

  if (!pendingLoading && !currentUser) {
    return <>{children}</>;
  }

  return (
    <ForceOnboarding
      pendingSignupTasks={currentUser?.signupPending ?? []}
      signupComplete={currentUser?.signupComplete ?? false}
      pendingTasksLoading={pendingLoading}
      isTeacher={currentUser?.isTeacher ?? false}
    >
      {children}
    </ForceOnboarding>
  );
}
